(ns hypnos.backoffice.tile.table.recommendation
  "The recommendations table."
  (:require [clojure.string :as str]
            [hypnos.backoffice.core.auth :as auth]
            [hypnos.graphql.mutations :as mutations]
            [hypnos.i18n :refer [tr]]
            [hypnos.lookup :refer (all-makes-by-manufacturer-id
                                   all-models-by-manufacturer-id-make-id
                                   lookup-manufacturer-by-id
                                   lookup-make-by-id
                                   lookup-model-by-id
                                   lookup-mask-by-model-id)]
            [hypnos.translator.consult :as t-c]
            [hypnos.translator.patient :as t-p]
            [medley.core :as mc]
            [re-frame.core :as rf]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- assoc-mask-category
  "Add mask category from"
  [catalog recommendation]
  (let [ids  (lookup-mask-by-model-id catalog (get-in recommendation [:recommendation/model :model/id]))
        make (lookup-make-by-id catalog (:manufacturer/id ids) (:make/id ids))]
    (assoc recommendation :mask-category (:make/category make))))

(defn- header
  []
  [:tr
   [:th]
   [:th (tr :ui.label/category)]
   [:th (tr :ui.label/manufacturer)]
   [:th (tr :ui.label/mask)]
   [:th (tr :ui.label/size)]
   [:th (tr :ui.label/fit-score)]
   [:th]])

(defn- select-row
  [state _idx recommendation manufacturer make model enable?]
  [:tr
   [:td]
   [:td (str (tr :ui/mask-category (:make/category make)))]
   [:td (str (:manufacturer/name manufacturer))]
   [:td (str (:make/name make))]
   [:td (str (:model/name model))]
   [:td (str (:recommendation/confidence-min recommendation))]
   [:td (when enable?
          [:div.buttons.is-right.has-addons
           [:button.button.is-info
            {:on-click (fn [_]
                         (let [patient (dissoc (t-p/patient->db @state)
                                               :patient/last-consult
                                               :patient/image
                                               :patient/anatomy
                                               :patient/recommendations
                                               :patient/mask
                                               :patient/selector
                                               :patient/selector-type
                                               :consult/_patient
                                               :communication/_recipient)
                               consult (dissoc (t-p/consult->db @state)
                                               :consult/user
                                               :consult/manufacturer
                                               :consult/make
                                               :consult/model)]
                           (swap! state assoc-in [:patient :patient/id :value] nil)
                           (rf/dispatch-sync [:amplify/mutation
                                              mutations/select-mask
                                              {:patient        (t-p/patient->gql patient)
                                               :consult        (t-p/consult->gql consult)
                                               :recommendation (t-c/recommendation->gql recommendation)}
                                              :backoffice/refresh-patient
                                              :backoffice/noop])
                           (swap! state assoc
                                  :new-consult? false
                                  :new-exchange? false)))}
            [:span (tr :ui.label/select)]]])]
   [:td]])

(defn- manufacturer-field
  "The manufacturer field"
  [catalog state idx]
  (let [ids          (lookup-mask-by-model-id @catalog (get-in @state [:recommendations :system idx :recommendation/model]))
        manufacturer (lookup-manufacturer-by-id @catalog (:manufacturer/id ids))]
    [:div.field.is-narrow
     [:div.control
      [:div.select
       [:select
        {:type      :text
         :value     (if-let [val (get-in @state [:recommendations :system idx :recommendation/manufacturer])]
                      val
                      (if-let [manufacturer-id (:manufacturer/id manufacturer)]
                        (do
                          (swap! state assoc-in [:recommendations :system idx :recommendation/manufacturer] manufacturer-id)
                          manufacturer-id)
                        ""))
         :on-change (fn [e]
                      (let [old-value (get-in @state [:recommendations :system idx :recommendation/manufacturer])
                            new-value (-> e .-target .-value)
                            new-value (when-not (= "" new-value)
                                        new-value)
                            changed?  (not= new-value old-value)]
                        (swap! state assoc-in
                               [:recommendations :system idx :recommendation/manufacturer]
                               (when new-value (mc/uuid new-value)))
                        (when changed?
                          (swap! state assoc-in [:recommendations :system idx :recommendation/make] nil)
                          (swap! state assoc-in [:recommendations :system idx :recommendation/model] nil)
                          (swap! state assoc-in [:recommendations :system idx :recommendation/confidence-min] nil)
                          (swap! state assoc-in [:recommendations :system idx :recommendation/confidence-max] nil))))}
        [:option {:value ""} ""]
        (doall
         (for [m (sort-by :manufacturer/name @catalog)]
           [:option
            {:key   (:manufacturer/id m)
             :value (:manufacturer/id m)}
            (:manufacturer/name m)]))]]]]))

(defn- make-field
  "The make field."
  [catalog state idx]
  (let [ids          (lookup-mask-by-model-id @catalog (get-in @state [:recommendations :system idx :recommendation/model]))
        manufacturer (lookup-manufacturer-by-id @catalog (:manufacturer/id ids))
        make         (lookup-make-by-id @catalog (:manufacturer/id ids) (:make/id ids))]
    [:div.field.is-narrow
     [:div.control
      [:div.select
       [:select
        {:type      :text
         :disabled  (nil? (get-in @state [:recommendations :system idx :recommendation/manufacturer]))
         :value     (if-let [val (get-in @state [:recommendations :system idx :recommendation/make])]
                      val
                      (if-let [make-id (:make/id make)]
                        (do
                          (swap! state assoc-in [:recommendations :system idx :recommendation/make] make-id)
                          make-id)
                        ""))
         :on-change (fn [e]
                      (let [old-value (get-in @state [:recommendations :system idx :recommendation/make])
                            new-value (-> e .-target .-value)
                            new-value (when-not (= "" new-value) new-value)
                            changed?  (not= new-value old-value)]
                        (swap! state assoc-in
                               [:recommendations :system idx :recommendation/make]
                               (when new-value (mc/uuid new-value)))
                        (when changed?
                          (swap! state assoc-in [:recommendations :system idx :recommendation/model] nil)
                          (swap! state assoc-in [:recommendations :system idx :recommendation/confidence-min] nil)
                          (swap! state assoc-in [:recommendations :system idx :recommendation/confidence-max] nil))))}
        [:option {:value ""} ""]
        (doall
         (for [m (sort-by :make/name
                          (all-makes-by-manufacturer-id
                           @catalog
                           (or (get-in @state [:recommendations :system idx :recommendation/manufacturer])
                               (:manufacturer/id manufacturer))))]
           [:option {:key (:make/id m) :value (:make/id m)} (:make/name m)]))]]]]))

(defn- model-field
  "The model field."
  [catalog state idx]
  (let [ids          (lookup-mask-by-model-id @catalog (get-in @state [:recommendations :system idx :recommendation/model]))
        manufacturer (lookup-manufacturer-by-id @catalog (:manufacturer/id ids))
        make         (lookup-make-by-id @catalog (:manufacturer/id ids) (:make/id ids))]
    [:div.field.is-narrow
     [:div.control
      [:div.select
       [:select
        {:type      :text
         :disabled  (or (nil? (get-in @state [:recommendations :system idx :recommendation/manufacturer]))
                        (nil? (get-in @state [:recommendations :system idx :recommendation/make])))
         :value     (if-let [val (get-in @state [:recommendations :system idx :recommendation/model])]
                      val
                      "")
         :on-change (fn [e]
                      (let [old-value (get-in @state [:recommendations :system idx :recommendation/model])
                            new-value (-> e .-target .-value)
                            new-value (when-not (= "" new-value) new-value)
                            changed?  (not= new-value old-value)]
                        (swap! state assoc-in
                               [:recommendations :system idx :recommendation/model]
                               (when new-value (mc/uuid new-value)))
                        (when changed?
                          (swap! state assoc-in [:recommendations :system idx :recommendation/confidence-min] nil)
                          (swap! state assoc-in [:recommendations :system idx :recommendation/confidence-max] nil))))}
        [:option {:value ""} ""]
        (doall
         (for [m (sort-by :model/name
                          (all-models-by-manufacturer-id-make-id
                           @catalog
                           (or (get-in @state [:recommendations :system idx :recommendation/manufacturer])
                               (:manufacturer/id manufacturer))
                           (or (get-in @state [:recommendations :system idx :recommendation/make])
                               (:make/id make))))]
           [:option {:key (:model/id m) :value (:model/id m)} (:model/name m)]))]]]]))

(defn- fit-score-field
  "The fit score field."
  [state idx]
  [:div.field.is-narrow
   [:div.control
    [:div.select
     [:select
      {:type      :text
       :disabled  (or (nil? (get-in @state [:recommendations :system idx :recommendation/manufacturer]))
                      (nil? (get-in @state [:recommendations :system idx :recommendation/make]))
                      (nil? (get-in @state [:recommendations :system idx :recommendation/model])))
       :value     (if-let [val (get-in @state [:recommendations :system idx :recommendation/confidence-min])]
                    val
                    (float 0))
       :on-change (fn [e]
                    (let [val       (-> e .-target .-value)
                          new-value (when-not (str/blank? val) (js/parseFloat val))]
                      (swap! state assoc-in
                             [:recommendations :system idx :recommendation/confidence-min]
                             new-value)
                      (swap! state assoc-in
                             [:recommendations :system idx :recommendation/confidence-max]
                             new-value)))}
      (doall
       (for [score (range 0 (float 90))]
         [:option {:key score :value score} score]))]]]])

(defn- recommendation-row
  "A recommendation row "
  [catalog state idx]
  [:tr
   [:td]
   [:td [manufacturer-field catalog state idx]]
   [:td [make-field catalog state idx]]
   [:td [model-field catalog state idx]]
   [:td [fit-score-field state idx]]
   [:td]])

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Tables

(defn recommendation-table
  "Table containing the recommendations table for the superuser."
  [state]
  (let [catalog (rf/subscribe [:backoffice/catalog])]
    [:div.table-container
     [:table.table.is-fullwidth.is-striped.is-hoverable.is-sortable.is-fullwidth
      [:thead
       [:tr
        [:th]
        [:th (tr :ui.label/manufacturer)]
        [:th (tr :ui.label/mask)]
        [:th (tr :ui.label/size)]
        [:th (tr :ui.label/fit-score)]
        [:th]]]
      [:tbody
       (let [indexed-recs (->> (get-in @state [:recommendations :system])
                               (mapv (partial assoc-mask-category @catalog))
                               (map-indexed vector))]
         (doall
          (for [[idx recommendation] indexed-recs]
            ^{:key (:recommendation/id recommendation)} [recommendation-row catalog state idx])))]]]))

(defn select-table
  "The recommendations selection table for the therapist."
  [state]
  (let [auth    (rf/subscribe [:backoffice/auth])
        catalog (rf/subscribe [:backoffice/catalog])]
    [:div.table-container
     [:table.table.is-fullwidth.is-striped.is-hoverable.is-sortable.is-fullwidth
      [:thead
       [header]]
      [:tbody
       (let [enable?      (and (auth/authorized? @auth :select-recommendation)
                               (contains? #{:consult-state/pending}
                                          (get-in @state [:patient :patient/last-consult :consult/status :value])))
             indexed-recs (->> (get-in @state [:recommendations :system])
                               (remove #(nil? (:recommendation/model %)))
                               (mapv (partial assoc-mask-category @catalog))
                               (map-indexed vector))]
         (doall
          (for [[idx recommendation] indexed-recs]
            (let [ids          (lookup-mask-by-model-id @catalog (:recommendation/model recommendation))
                  manufacturer (lookup-manufacturer-by-id @catalog (:manufacturer/id ids))
                  make         (lookup-make-by-id @catalog (:manufacturer/id ids) (:make/id ids))
                  model        (lookup-model-by-id @catalog (:manufacturer/id ids) (:make/id ids) (:model/id ids))]
              ^{:key (:recommendation/id recommendation)}
              [select-row state idx recommendation manufacturer make model enable?]))))]]]))
